/**
 * F18 — NIPE tool: setFormField
 *
 * UI tool. Sets a field on the wizard's react-hook-form to a NIPE-
 * suggested value. The user sees the field update immediately and can
 * edit it before submitting. No remote mutation.
 *
 * Per Round 1 lock: NIPE may pre-fill freely; if the user overrides,
 * NIPE accepts the new value silently (no special error UI, no audit
 * log of disagreement).
 */

import type { NipeTool } from './types';

export type OnboardFormFieldName =
  | 'firmName'
  | 'slug'
  | 'practiceArea'
  | 'theme'
  | 'primaryColor'
  | 'tenantDomain';

export interface SetFormFieldContext {
  /** Optional. Only present when the SPA has the F18 wizard mounted. */
  setFormField?: (
    name: OnboardFormFieldName,
    value: string,
  ) => Promise<{ ok: boolean; previousValue: string | null }>;
}

const ALLOWED_FIELDS: OnboardFormFieldName[] = [
  'firmName',
  'slug',
  'practiceArea',
  'theme',
  'primaryColor',
  'tenantDomain',
];

export const setFormFieldTool: NipeTool<SetFormFieldContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'setFormField',
      description:
        'Pre-fill a wizard form field with a suggested value. The user can edit or override freely.',
      parameters: {
        type: 'object',
        properties: {
          name: {
            type: 'string',
            description:
              'Field name. One of firmName, slug, practiceArea, theme, primaryColor, tenantDomain.',
          },
          value: {
            type: 'string',
            description: 'Suggested value.',
          },
        },
        required: ['name', 'value'],
      },
    },
  },
  mutatesRemote: false,
  requiresApproval: false,
  handler: async (args, ctx) => {
    if (!ctx.setFormField) {
      return { error: 'Onboard wizard is not currently mounted.' };
    }
    const name =
      typeof args.name === 'string' &&
      (ALLOWED_FIELDS as string[]).includes(args.name)
        ? (args.name as OnboardFormFieldName)
        : null;
    if (!name) {
      return {
        error: `Unknown field name. Allowed: ${ALLOWED_FIELDS.join(', ')}`,
      };
    }
    const value = typeof args.value === 'string' ? args.value : '';
    return await ctx.setFormField(name, value);
  },
};
